import * as THREE from 'three';

export class BrushSystem {
    constructor(texturePainter) {
        this.texturePainter = texturePainter;
        
        this.brushSize = 10;
        this.brushOpacity = 1.0;
        this.brushHardness = 0.8;
        this.brushColor = '#000000';
        this.brushMode = 'paint';
        this.brushTexture = null;
        
        this.isPainting = false;
        this.isErasing = false;
        this.lastPaintPosition = null;
        this.paintStrokes = [];
        
        this.strokeCanvases = new Map();
        this.strokeStarted = false;
        
        this.paintThrottleDelay = 0;
        this.lastPaintTime = 0;
        this.paintPending = false;

        this.minScreenSpacingPx = 0.5;
        
        this.meshUpdateThrottleDelay = 8;
        this.lastMeshUpdateTime = 0;
        this.meshUpdatePending = false;
        this.pendingTileUpdates = new Set();

        this.maxFramesBetweenMeshUpdates = 1;
        this._lastMeshUpdateFrame = 0;

        this._tmpIntersection = { uv: new THREE.Vector2(), point: new THREE.Vector3(), face: null };
        
        // Brush no longer owns the global undo stack. Use texturePainter.undoManager instead.
        // Keep lightweight local arrays for stroke visualization only.
        this.undoStates = undefined;
        this.maxUndoStates = undefined;
        this.pushUndoCheckpoint = undefined;
        this.undo = undefined;
        
        this.strokeCount = 0;
        this.memoryCleanupInterval = 50;
        
        this.webglSafeMode = false;
        this.contextLossCount = 0;
        
        this.mouse = new THREE.Vector2();
        this.raycaster = new THREE.Raycaster();
        
        this.brushCursor = null;
        this.cursorEnabled = true;
        this.lastCursorUpdate = 0;
        
        this.strokeInterpolation = true;
        this.strokeSamples = 1;

        this._rafId = 0;
        this._moveQueue = [];
        this._isPointerDown = false;

        // Track whether this stroke actually mutated any canvases to ensure an undo checkpoint
        this._strokeCommitted = false;
        
        this.initializeBrushSystem();
    }
    
    initializeBrushSystem() {
        this.createDefaultBrushTexture();
        this.setupBrushCursor();
        this.bindEvents();
    }
    
    createDefaultBrushTexture() {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        const gradient = ctx.createRadialGradient(
            size/2, size/2, 0,
            size/2, size/2, size/2
        );
        gradient.addColorStop(0, 'rgba(255,255,255,1)');
        gradient.addColorStop(0.7, 'rgba(255,255,255,0.8)');
        gradient.addColorStop(1, 'rgba(255,255,255,0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
        
        this.brushTexture = new THREE.CanvasTexture(canvas);
        this.brushTexture.needsUpdate = true;
    }
    
    setupBrushCursor() {
        const cursorGeometry = new THREE.RingGeometry(0.98, 1.0, 32);
        const cursorMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            transparent: true,
            opacity: 0.6,
            side: THREE.DoubleSide,
            depthTest: false
        });
        
        this.brushCursor = new THREE.Mesh(cursorGeometry, cursorMaterial);
        this.brushCursor.visible = false;
        this.texturePainter.scene.add(this.brushCursor);
    }
    
    bindEvents() {
        const canvas = this.texturePainter.renderer.domElement;
        
        canvas.addEventListener('webglcontextlost', (e) => {
            console.warn('BrushSystem: WebGL context lost, preventing default');
            e.preventDefault();
            this.stopPainting();
            
            this.contextLossCount++;
            if (this.contextLossCount >= 2) {
                this.webglSafeMode = true;
                console.warn('BrushSystem: Multiple context losses detected - enabling safe mode');
            }
        });
        
        canvas.addEventListener('webglcontextrestored', (e) => {
            this.initializeBrushSystem();
            
            if (this.webglSafeMode) {
                this.brushSize = Math.min(this.brushSize, 30);
                this.paintThrottleDelay = Math.max(this.paintThrottleDelay, 100);
            }
        });
        
        canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
        canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
        canvas.addEventListener('mouseup', (e) => this.onMouseUp(e));
        canvas.addEventListener('mouseleave', (e) => this.onMouseLeave(e));
        
        canvas.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
        canvas.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
        canvas.addEventListener('touchend', (e) => this.onTouchEnd(e));
        
        canvas.addEventListener('contextmenu', (e) => e.preventDefault());

        const loop = () => {
            this._rafId = 0;

            if (this._moveQueue.length) {
                const last = this._moveQueue[this._moveQueue.length - 1];
                this.updateMousePosition(last);
                
                const isUsingOrbitControls = this.texturePainter.controls && 
                    (this.texturePainter.controls.enabled && 
                    (last.buttons === 2 || last.buttons === 4));
                if (!isUsingOrbitControls) {
                    this.updateBrushCursor(last);
                } else {
                    this.hideBrushCursor();
                }

                if (this.isPainting || this.isErasing) {
                    this.paint(last);
                    this.lastPaintTime = performance.now();
                }
                this._moveQueue.length = 0;
            }

            if (this._isPointerDown || (this.cursorEnabled && this.brushCursor && this.brushCursor.visible)) {
                this._rafId = requestAnimationFrame(loop);
            }
        };

        // Helper to ensure the loop is running
        this._ensureRAF = () => {
            if (!this._rafId) {
                this._rafId = requestAnimationFrame(loop);
            }
        };
    }

    cleanupMemory() {
        try {
            if (window.gc) {
                window.gc();
            }
            
            if (this.undoStates.length > this.maxUndoStates / 2) {
                this.undoStates = this.undoStates.slice(-Math.floor(this.maxUndoStates / 2));
            }
        } catch (error) {
            console.warn('Memory cleanup failed:', error);
        }
    }

    
    onMouseDown(event) {
        if (!this.isEnabled() || this.texturePainter.transformControls?.object) return;
        
        if (event.button === 2) {
            this.hideBrushCursor();
            if (this.texturePainter.controls) {
                this.texturePainter.controls.enabled = true;
            }
            return;
        }
        
        this.raycaster.setFromCamera(this.mouse, this.texturePainter.camera);
        const intersects = this.raycaster.intersectObject(this.texturePainter.mesh, true);
        
        if (intersects.length > 0) {
            if (event.button === 0) {
                if (this.brushMode === 'erase') {
                    this.startErasing(event);
                } else {
                    this.startPainting(event);
                }
            }
        } else {
            if (this.texturePainter.controls) {
                this.texturePainter.controls.enabled = true;
            }
        }

                this._isPointerDown = true;
                this._ensureRAF();
    }
    
    onMouseMove(event) {
        this._moveQueue.push({
            clientX: event.clientX,
            clientY: event.clientY,
            buttons: event.buttons || 0
        });
        this._ensureRAF();
    }
    
    onMouseUp(event) {
        if (this.isPainting || this.isErasing) {
            this.stopPainting();
        }
        
        if (this.texturePainter.controls && this.texturePainter.projectionMode === 'brush') {
            this.texturePainter.controls.enabled = true;
        }

        this._isPointerDown = false;
    }
    
    onMouseLeave(event) {
        this.stopPainting();
        this.hideBrushCursor();
        
        if (this.texturePainter.controls) {
            this.texturePainter.controls.enabled = true;
        }
    }
    
    onTouchStart(event) {
        if (!this.isEnabled()) return;
        event.preventDefault();
        
        const touch = event.touches[0];
        const mouseEvent = new MouseEvent('mousedown', {
            clientX: touch.clientX,
            clientY: touch.clientY,
            button: 0
        });
        this.onMouseDown(mouseEvent);
    }
    
    onTouchMove(event) {
        event.preventDefault();
        
        const touch = event.touches[0];
        this._moveQueue.push({
            clientX: touch.clientX,
            clientY: touch.clientY,
            buttons: 1
        });
        this._ensureRAF();
    }
    
    onTouchEnd(event) {
        event.preventDefault();
        this.stopPainting();
        
        if (this.texturePainter.controls) {
            this.texturePainter.controls.enabled = true;
        }
    }
    
    updateMousePosition(event) {
        const rect = this.texturePainter.renderer.domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }
    
    updateBrushCursor(event) {
        if (!this.cursorEnabled || !this.isEnabled()) return;
        
        if (this.texturePainter.controls && 
            this.texturePainter.controls.enabled && 
            (event.buttons === 2 || event.buttons === 4)) {
            this.hideBrushCursor();
            return;
        }
        
        const now = performance.now();
        if (this.lastCursorUpdate && now - this.lastCursorUpdate < 16) {
            return;
        }
        this.lastCursorUpdate = now;
        
        this.raycaster.setFromCamera(this.mouse, this.texturePainter.camera);
        const intersects = this.raycaster.intersectObject(this.texturePainter.mesh, true);
        
        if (intersects.length > 0) {
            const intersection = intersects[0];
            this.brushCursor.position.copy(intersection.point);
            
            const normal = intersection.face ? intersection.face.normal.clone() : new THREE.Vector3(0, 0, 1);
            const worldNormal = normal.transformDirection(intersection.object.matrixWorld).normalize();
            this.brushCursor.lookAt(intersection.point.clone().add(worldNormal));
            
            const worldSpaceBrushSize = this.calculateUVAwareBrushSize(intersection);
            this.brushCursor.scale.setScalar(worldSpaceBrushSize);
            
            this.brushCursor.visible = true;
        } else {
            this.brushCursor.visible = false;
        }
    }
    
    calculateUVAwareBrushSize(intersection) {
        if (!intersection.face || !intersection.object.geometry) {
            const textureResolution = this.texturePainter.textureResolution || 1024;
            const brushSizeUV = this.brushSize / textureResolution;
            const meshScale = this.texturePainter.mesh ? this.texturePainter.mesh.scale.x : 1;
            return brushSizeUV * meshScale * 2;
        }
        
        const face = intersection.face;
        const geometry = intersection.object.geometry;
        const positionAttr = geometry.attributes.position;
        const uvAttr = geometry.attributes.uv;
        
        if (!uvAttr) {
            const textureResolution = this.texturePainter.textureResolution || 1024;
            const brushSizeUV = this.brushSize / textureResolution;
            const meshScale = this.texturePainter.mesh ? this.texturePainter.mesh.scale.x : 1;
            return brushSizeUV * meshScale * 2;
        }
        
        const v1 = new THREE.Vector3().fromBufferAttribute(positionAttr, face.a);
        const v2 = new THREE.Vector3().fromBufferAttribute(positionAttr, face.b);
        const v3 = new THREE.Vector3().fromBufferAttribute(positionAttr, face.c);
        
        const worldMatrix = intersection.object.matrixWorld;
        v1.applyMatrix4(worldMatrix);
        v2.applyMatrix4(worldMatrix);
        v3.applyMatrix4(worldMatrix);
        
        const uv1 = new THREE.Vector2().fromBufferAttribute(uvAttr, face.a);
        const uv2 = new THREE.Vector2().fromBufferAttribute(uvAttr, face.b);
        const uv3 = new THREE.Vector2().fromBufferAttribute(uvAttr, face.c);
        
        const worldEdge1 = v2.distanceTo(v1);
        const worldEdge2 = v3.distanceTo(v1);
        
        const uvEdge1 = uv2.distanceTo(uv1);
        const uvEdge2 = uv3.distanceTo(uv1);
        
        let uvDensity1 = uvEdge1 > 0.001 ? worldEdge1 / uvEdge1 : 1;
        let uvDensity2 = uvEdge2 > 0.001 ? worldEdge2 / uvEdge2 : 1;
        
        const avgUVDensity = Math.max(0.001, Math.min(100, (uvDensity1 + uvDensity2) / 2));
        
        const textureResolution = this.texturePainter.textureResolution || 1024;
        const brushSizeUV = this.brushSize / textureResolution; // Brush radius in UV space
        const worldSpaceBrushSize = brushSizeUV * avgUVDensity; // Convert to world space
        
        return worldSpaceBrushSize;
    }
    
    hideBrushCursor() {
        if (this.brushCursor) {
            this.brushCursor.visible = false;
        }
    }
    
    startPainting(event) {
        // Snapshot pre-stroke state to guarantee an undo point even if stopPainting is missed.
        // This mirrors common DCC behavior: undo returns to state before the stroke began.
        this.texturePainter?.undoManager?.pushCheckpoint?.();

        this.strokeCount++;
        if (this.strokeCount % this.memoryCleanupInterval === 0) {
            this.cleanupMemory();
        }
        
        if (this.texturePainter.controls) {
            this.texturePainter.controls.enabled = false;
        }
        
        this.isPainting = true;
        // During an active stroke, allow faster compositing to the mesh
        this._prevMeshUpdateThrottleDelay = this.meshUpdateThrottleDelay;
        this.meshUpdateThrottleDelay = 0; // push updates as fast as rAF allows during stroke
        this.isErasing = false;
        this.lastPaintPosition = null;
        this.strokeStarted = false;
        this._strokeCommitted = false;
        this.paintStrokes.push({ type: this.brushMode, points: [] });
        
        this.paint(event);
    }
    
    startErasing(event) {
        // Snapshot pre-stroke state to guarantee an undo point even if stopPainting is missed.
        this.texturePainter?.undoManager?.pushCheckpoint?.();

        this.strokeCount++;
        if (this.strokeCount % this.memoryCleanupInterval === 0) {
            this.cleanupMemory();
        }
        
        if (this.texturePainter.controls) {
            this.texturePainter.controls.enabled = false;
        }
        
        this.isErasing = true;
        // During an active erase stroke, allow faster compositing
        this._prevMeshUpdateThrottleDelay = this.meshUpdateThrottleDelay;
        this.meshUpdateThrottleDelay = 0;
        this.isPainting = false;
        this.lastPaintPosition = null;
        this.strokeStarted = false;
        this._strokeCommitted = false;
        this.paintStrokes.push({ type: this.brushMode, points: [] });
        
        this.paint(event);
    }
    
    continuePainting(event) {
        if (!this.isPainting && !this.isErasing) return;

        // Coalesce high-frequency input into a single paint per animation frame
        if (!this.paintPending) {
            this.paintPending = true;
            requestAnimationFrame(() => {
                if (this.isPainting || this.isErasing) {
                    this.paint(event);
                    this.lastPaintTime = performance.now();
                }
                this.paintPending = false;
            });
        }
    }
    
    stopPainting() {
        this.isPainting = false;
        this.isErasing = false;
        this.lastPaintPosition = null;
        this.strokeStarted = false;
        this.strokeCanvases.clear();
        
        if (this.pendingTileUpdates.size > 0) {
            this.executeMeshUpdate();
        }

        // Restore throttling after stroke finishes to reduce overall work
        if (typeof this._prevMeshUpdateThrottleDelay === 'number') {
            this.meshUpdateThrottleDelay = this._prevMeshUpdateThrottleDelay;
            this._prevMeshUpdateThrottleDelay = undefined;
        }
        
        if (this.texturePainter.controls) {
            this.texturePainter.controls.enabled = true;
        }
    }
    
    paint(event) {
        if (!this.isEnabled()) {
            return;
        }

        // Use the latest queued event to compute accurate mouse position before raycast
        if (event && typeof event.clientX === 'number' && typeof event.clientY === 'number') {
            this.updateMousePosition(event);
        }
        
        this.raycaster.setFromCamera(this.mouse, this.texturePainter.camera);
        const intersects = this.raycaster.intersectObject(this.texturePainter.mesh, true);

        if (intersects.length === 0) {
            return;
        }
        
        const intersection = intersects[0];
        if (!intersection.uv) return;

        const alpha = 0.35;
        if (this.lastPaintPosition && this.lastPaintPosition.uv) {
            const prev = this.lastPaintPosition.uv;
            const curr = intersection.uv.clone();

            const prevTileU = Math.floor(prev.x), prevTileV = Math.floor(prev.y);
            const currTileU = Math.floor(curr.x), currTileV = Math.floor(curr.y);

            const duAbs = Math.abs(prev.x - curr.x);
            const dvAbs = Math.abs(prev.y - curr.y);
            const largeJump = (duAbs > 0.25 || dvAbs > 0.25);

            if (prevTileU === currTileU && prevTileV === currTileV && !largeJump) {
                const suv = new THREE.Vector2(
                    prev.x + (curr.x - prev.x) * alpha,
                    prev.y + (curr.y - prev.y) * alpha
                );
                intersection.uv = suv;
            } else {
                // seam: keep curr as-is; chain reset happens in interpolatePaint
            }
        }

        this.paintAtUV(intersection);
        
        if (this.paintStrokes.length > 0) {
            this.paintStrokes[this.paintStrokes.length - 1].points.push({
                uv: intersection.uv.clone(),
                point: intersection.point.clone(),
                normal: intersection.face ? intersection.face.normal.clone() : new THREE.Vector3(0, 0, 1)
            });
        }
    }
    
    paintAtUV(intersection) {
        const texRes = this.texturePainter.textureResolution || 1024;
        const brushUVRadius = this.brushSize / texRes;
        const spacingFactor = 0.35;
        const desiredUVStep = Math.max(0.00025, Math.min(0.25, 2 * brushUVRadius * spacingFactor));

        const rect = this.texturePainter.renderer.domElement.getBoundingClientRect();
        const toScreen = (uv) => {
            return new THREE.Vector2(
                ((this.mouse.x + 1) * 0.5) * rect.width,
                ((-this.mouse.y + 1) * 0.5) * rect.height
            );
        };

        if (this.lastPaintPosition && intersection.uv && this.lastPaintPosition.uv) {
            const uvA = this.lastPaintPosition.uv;
            const uvB = intersection.uv;
            const du = Math.abs(uvA.x - uvB.x);
            const dv = Math.abs(uvA.y - uvB.y);
            const distUV = Math.hypot(du, dv);

            const minScreenSpacingPx = Math.max(0.75, this.minScreenSpacingPx || 0.5);
            const prevScreen = toScreen(this.lastPaintPosition.uv);
            const currScreen = toScreen(intersection.uv);
            const distScreen = prevScreen.distanceTo(currScreen);

            // Do not cull the very first dab in a stroke purely due to spacing filters
            if (this.strokeStarted && distUV < desiredUVStep && distScreen < minScreenSpacingPx) {
                return;
            }
        }

        // Interpolate with spacing-aware stepping to avoid clustering dabs too closely.
        if (this.lastPaintPosition) {
            const a = this.lastPaintPosition.uv, b = intersection.uv;
            const aTileU = Math.floor(a.x), aTileV = Math.floor(a.y);
            const bTileU = Math.floor(b.x), bTileV = Math.floor(b.y);
            const duAbs = Math.abs(a.x - b.x), dvAbs = Math.abs(a.y - b.y);
            const largeJump = (duAbs > 0.25 || dvAbs > 0.25);

            if (aTileU !== bTileU || aTileV !== bTileV || largeJump) {
                this.paintSinglePoint(intersection);
            } else {
                this.interpolatePaint(this.lastPaintPosition, intersection, desiredUVStep);
            }
        } else {
            this.paintSinglePoint(intersection);
        }

        this.lastPaintPosition = {
            uv: intersection.uv.clone(),
            point: intersection.point.clone(),
            face: intersection.face
        };
        // Mark that we've successfully stamped at least once this stroke (used by spacing filter)
        this.strokeStarted = true;
    }
    
    interpolatePaint(lastIntersection, currentIntersection, desiredUVStep) {
        const uvA = lastIntersection.uv;
        const uvB = currentIntersection.uv;

        const tileA = { u: Math.floor(uvA.x), v: Math.floor(uvA.y) };
        const tileB = { u: Math.floor(uvB.x), v: Math.floor(uvB.y) };
        if (tileA.u !== tileB.u || tileA.v !== tileB.v) {
            this._tmpIntersection.uv.copy(currentIntersection.uv);
            this._tmpIntersection.point.copy(currentIntersection.point);
            this._tmpIntersection.face = currentIntersection.face;
            this.paintSinglePoint(this._tmpIntersection);

            this.lastPaintPosition = {
                uv: currentIntersection.uv.clone(),
                point: currentIntersection.point.clone(),
                face: currentIntersection.face
            };
            return;
        }

        // 2) Abort bridging if different UV islands within the same tile (heuristic via face normal discontinuity)
        // If either intersection lacks face info, fall back to UV gap heuristic
        const faceA = lastIntersection.face;
        const faceB = currentIntersection.face;
        if (faceA && faceB) {
            // Compare world-space normals; large angle indicates a likely island/UV seam
            const nA = faceA.normal.clone();
            const nB = faceB.normal.clone();
            const worldA = nA.transformDirection(lastIntersection.object?.matrixWorld || this.texturePainter.mesh.matrixWorld).normalize();
            const worldB = nB.transformDirection(currentIntersection.object?.matrixWorld || this.texturePainter.mesh.matrixWorld).normalize();
            const dot = worldA.dot(worldB);
            // Threshold: ~cos(25deg) ≈ 0.9. If normals diverge more than this, treat as different island.
            if (dot < 0.9) {
                this._tmpIntersection.uv.copy(currentIntersection.uv);
                this._tmpIntersection.point.copy(currentIntersection.point);
                this._tmpIntersection.face = currentIntersection.face;
                this.paintSinglePoint(this._tmpIntersection);

                // Break interpolation chain across island seam to avoid long screen-space trail artifacts
                this.lastPaintPosition = {
                    uv: currentIntersection.uv.clone(),
                    point: currentIntersection.point.clone(),
                    face: currentIntersection.face
                };
                return;
            }
        }

        // 3) UV-space gap thresholds
        const du = Math.abs(uvA.x - uvB.x);
        const dv = Math.abs(uvA.y - uvB.y);
        const distUV = Math.hypot(du, dv);

        // Hard abort for excessively large UV gap (likely a discontinuity even within tile or between islands)
        const hardMaxFillGapUV = 0.02; // stricter: 2% of a tile
        if (distUV > hardMaxFillGapUV) {
            this._tmpIntersection.uv.copy(currentIntersection.uv);
            this._tmpIntersection.point.copy(currentIntersection.point);
            this._tmpIntersection.face = currentIntersection.face;
            this.paintSinglePoint(this._tmpIntersection);

            // Reset chain to prevent bridging over large UV gaps (seams/edges)
            this.lastPaintPosition = {
                uv: currentIntersection.uv.clone(),
                point: currentIntersection.point.clone(),
                face: currentIntersection.face
            };
            return;
        }

        // 4) Step along the segment with spacing based on desiredUVStep.
        // Use a max step cap and break early when step distance is small to avoid overwork.
        const steps = Math.min(48, Math.max(1, Math.ceil(distUV / desiredUVStep)));

        const pA = lastIntersection.point;
        const pB = currentIntersection.point;

        let lastStampedUV = uvA.clone();
        for (let i = 0; i <= steps; i++) {
            const t = i / steps;

            const u = THREE.MathUtils.lerp(uvA.x, uvB.x, t);
            const v = THREE.MathUtils.lerp(uvA.y, uvB.y, t);
            const duStep = Math.abs(u - lastStampedUV.x);
            const dvStep = Math.abs(v - lastStampedUV.y);
            const distStep = Math.hypot(duStep, dvStep);
            if (distStep < desiredUVStep * 0.5 && i !== steps) {
                continue; // skip overly dense stamps
            }

            // Abort if stepping crosses into a different tile within the segment
            const stepTileU = Math.floor(u);
            const stepTileV = Math.floor(v);
            const baseTileU = Math.floor(uvA.x);
            const baseTileV = Math.floor(uvA.y);
            if (stepTileU !== baseTileU || stepTileV !== baseTileV) {
                // Stop interpolation at seam
                break;
            }

            this._tmpIntersection.uv.set(u, v);
            this._tmpIntersection.point.set(
                THREE.MathUtils.lerp(pA.x, pB.x, t),
                THREE.MathUtils.lerp(pA.y, pB.y, t),
                THREE.MathUtils.lerp(pA.z, pB.z, t)
            );
            this._tmpIntersection.face = currentIntersection.face;

            this.paintSinglePoint(this._tmpIntersection);
            lastStampedUV.set(u, v);
        }
    }
    
    paintSinglePoint(intersection) {
        // Strict seam safety: only stamp to the tile actually hit by the raycast.
        // Do NOT splat to neighbors near edges, which can produce trails on seams.
        const uv = intersection.uv;
        const tileU = Math.floor(uv.x);
        const tileV = Math.floor(uv.y);
        const udimTile = this.texturePainter.calculateUDIMTile(tileU, tileV);
        const tileIndex = this.texturePainter.uvChannels.findIndex(ch => ch.udimTile === udimTile);
        if (tileIndex === -1) return;

        // Convert to local UV for that tile only
        const localUV = new THREE.Vector2(uv.x - tileU, uv.y - tileV);

        // Avoid stamping if localUV falls outside 0..1 due to precision/edge jitter
        if (localUV.x < 0 || localUV.x > 1 || localUV.y < 0 || localUV.y > 1) return;

        this.paintOnUDIMTile(tileIndex, localUV, intersection);
        this.pendingTileUpdates.add(tileIndex);

        this.throttledMeshUpdate();
    }
    
    throttledMeshUpdate() {
        const now = performance.now();

        // If delay is 0 (active stroke fast-path), execute on rAF without extra waiting,
        // but enforce a maximum number of frames between updates to avoid visual lag.
        if (this.meshUpdateThrottleDelay === 0) {
            const frameId = performance.now(); // use time as monotonic surrogate for frame id
            const framesSince = (this._lastMeshUpdateFrame === 0) ? 0 : (frameId - this._lastMeshUpdateFrame);
            // If we haven't updated in ~2 frames (~32ms at 60Hz), force an update.
            const forceNow = framesSince > 32;

            if (!this.meshUpdatePending || forceNow) {
                this.meshUpdatePending = true;
                requestAnimationFrame(() => {
                    this.executeMeshUpdate();
                    this.meshUpdatePending = false;
                });
            }
            return;
        }
        
        // Non-zero throttle path: reduce delay and still coalesce onto rAF
        if (now - this.lastMeshUpdateTime < this.meshUpdateThrottleDelay) {
            if (!this.meshUpdatePending) {
                this.meshUpdatePending = true;
                requestAnimationFrame(() => {
                    this.executeMeshUpdate();
                    this.meshUpdatePending = false;
                });
            }
            return;
        }
        
        this.executeMeshUpdate();
    }
    
    executeMeshUpdate() {
        if (this.pendingTileUpdates.size === 0) return;
        
        this.pendingTileUpdates.forEach(tileIndex => {
            this.texturePainter.compositeTexture(tileIndex);
        });
        
        this.pendingTileUpdates.clear();
        
        this.texturePainter.refreshCharacterMaterials();
        
        this.lastMeshUpdateTime = performance.now();
        this._lastMeshUpdateFrame = this.lastMeshUpdateTime;
    }
    
    // Deprecated neighbor stamping to prevent seam trails. Kept for reference.
    getAffectedUDIMTiles(uv) {
        // Only return the tile actually under the pointer to avoid any cross-seam stamping.
        const tileU = Math.floor(uv.x);
        const tileV = Math.floor(uv.y);
        const udimTile = this.texturePainter.calculateUDIMTile(tileU, tileV);
        const tileIndex = this.texturePainter.uvChannels.findIndex(channel => channel.udimTile === udimTile);
        if (tileIndex === -1) return [];
        const localUV = new THREE.Vector2(uv.x - tileU, uv.y - tileV);
        if (localUV.x < 0 || localUV.x > 1 || localUV.y < 0 || localUV.y > 1) return [];
        return [{ tileIndex, localUV, udimTile }];
    }
    
    paintOnUDIMTile(tileIndex, localUV) {
        const channel = this.texturePainter.uvChannels[tileIndex];
        if (!channel) return;

        const canvas = channel.decalCanvas;
        const marginPx = Math.max(1, Math.round(this.brushSize * 0.1)); // 10% of radius or at least 1px
        const marginU = marginPx / canvas.width;
        const marginV = marginPx / canvas.height;

        if (localUV.x < marginU || localUV.x > (1 - marginU) || localUV.y < marginV || localUV.y > (1 - marginV)) {
            return;
        }
        
        const ctx = channel.decalContext;

        const canvasX = localUV.x * canvas.width;
        const canvasY = (1 - localUV.y) * canvas.height; // Flip Y for canvas
        
        this.applyBrushStroke(ctx, canvasX, canvasY, canvas, tileIndex);
        // Defer WebGL texture upload to composite step to avoid per-dab GPU sync
        // channel.texture.needsUpdate = true;
    }
    
    applyBrushStroke(ctx, centerX, centerY, canvas, tileIndex) {
        const brushRadius = this.brushSize;
        const opacity = this.brushOpacity;

        // Ensure per-tile stroke buffers exist (used for both paint and erase for consistency)
        if (!this.strokeCanvases.has(tileIndex)) {
            const strokeCanvas = document.createElement('canvas');
            strokeCanvas.width = canvas.width;
            strokeCanvas.height = canvas.height;
            const strokeCtx = strokeCanvas.getContext('2d');
            strokeCtx.clearRect(0, 0, strokeCanvas.width, strokeCanvas.height);
            
            const baseCanvas = document.createElement('canvas');
            baseCanvas.width = canvas.width;
            baseCanvas.height = canvas.height;
            const baseCtx = baseCanvas.getContext('2d');
            baseCtx.drawImage(canvas, 0, 0);
            
            this.strokeCanvases.set(tileIndex, {
                canvas: strokeCanvas,
                context: strokeCtx,
                baseCanvas: baseCanvas
            });
        }

        const strokeData = this.strokeCanvases.get(tileIndex);

        // 1) Accumulate the current dab into stroke canvas with full strength in a mode-appropriate way
        // For paint: draw color into stroke canvas fully opaque; final opacity applied during composite.
        // For erase: accumulate a per-stroke MAX mask (white = erase) so overlaps within the same stroke don't compound.
        if (this.brushMode === 'paint') {
            strokeData.context.save();
            strokeData.context.globalAlpha = 1.0;
            strokeData.context.globalCompositeOperation = 'source-over';
            this.drawCircularBrush(strokeData.context, centerX, centerY, brushRadius);
            strokeData.context.restore();
        } else {
            // Build a dab mask (white alpha falloff) and union it into the stroke mask.
            // Using 'lighter' approximates max() under 8-bit clamping, preventing multi-dab amplification.
            const dab = document.createElement('canvas');
            dab.width = Math.max(2, Math.ceil(brushRadius * 2));
            dab.height = Math.max(2, Math.ceil(brushRadius * 2));
            const dabCtx = dab.getContext('2d');
            dabCtx.clearRect(0, 0, dab.width, dab.height);

            // Draw white falloff circle centered in the dab canvas
            const savedMode = this.brushMode;
            const savedColor = this.brushColor;
            const savedHardness = this.brushHardness;
            this.brushMode = 'paint';
            this.brushColor = '#ffffff';
            this.drawCircularBrush(dabCtx, dab.width * 0.5, dab.height * 0.5, brushRadius);
            this.brushMode = savedMode;
            this.brushColor = savedColor;
            this.brushHardness = savedHardness;

            // Union dab into stroke mask
            strokeData.context.save();
            strokeData.context.globalAlpha = 1.0;
            strokeData.context.globalCompositeOperation = 'lighter';
            strokeData.context.drawImage(dab, Math.round(centerX - dab.width * 0.5), Math.round(centerY - dab.height * 0.5));
            strokeData.context.restore();
        }

        // 2) Composite to the actual decal canvas from base + stroke using mode-appropriate blending
        ctx.save();
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw base first
        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(strokeData.baseCanvas, 0, 0);

        if (this.brushMode === 'paint') {
            // Paint: overlay stroke color buffer with stroke-level opacity
            ctx.globalAlpha = opacity;
            ctx.globalCompositeOperation = 'source-over';
            ctx.drawImage(strokeData.canvas, 0, 0);
        } else {
            // Erase: apply the accumulated mask as destination-out with stroke-level opacity in one composite
            ctx.globalAlpha = opacity;
            ctx.globalCompositeOperation = 'destination-out';
            ctx.drawImage(strokeData.canvas, 0, 0);
        }

        ctx.restore();

        // Mark that we actually mutated the decal canvas this stroke
        this._strokeCommitted = true;
    }
    
    drawCircularBrush(ctx, centerX, centerY, radius) {
        // Hardness semantics (revised):
        // hardness = 1.0 -> fully hard circle (no falloff)
        // hardness = 0.0 -> fully soft (all falloff)
        // hardness controls the solid core proportion linearly.
        // Remap UI hardness (0..1) to a perceptual curve so low values are softer.
        // A power curve reduces innerR for low-to-mid hardness to avoid "intense" edges.
        const hUI = Math.max(0, Math.min(1, this.brushHardness));
        // Much softer mapping: keep inner core tiny until higher hardness.
        // Piecewise curve for better control in low and mid ranges:
        //   0..0.5 -> quadratic (very soft), 0.5..1 -> cubic ramp.
        const hSoft = hUI <= 0.5
            ? 0.18 * (hUI / 0.5) * (hUI / 0.5)            // up to ~0.18 innerR at 50%
            : 0.18 + 0.82 * Math.pow((hUI - 0.5) / 0.5, 3); // smoothly reaches 1 near end
        const h = Math.min(1, Math.max(0, hSoft));
        const outerR = radius;
        const eps = 1e-3;
        let innerR = outerR * h;
        // Keep gradient well-defined; treat near-1 hardness as hard circle
        if (innerR > outerR - eps) innerR = outerR - eps;
        if (innerR < 0) innerR = 0;

        if (this.brushMode === 'erase') {
            if (this.webglSafeMode) {
                radius = Math.min(radius, 20);
            }

            const originalComposite = ctx.globalCompositeOperation;
            try {
                ctx.globalCompositeOperation = 'destination-out';

                // If effectively hard, erase as a solid circle (no falloff)
                if (outerR - innerR <= 2 * eps) {
                    ctx.fillStyle = 'rgba(255,255,255,1)';
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, outerR, 0, Math.PI * 2);
                    ctx.fill();
                } else {
                    const grad = ctx.createRadialGradient(centerX, centerY, innerR, centerX, centerY, outerR);
                    // Solid erase up to innerR, then fade to 0 at edge
                // Softer falloff: two mid stops with lower alpha to stretch the fade
                const tInner = Math.min(0.999, Math.max(0, innerR / outerR));
                grad.addColorStop(0, 'rgba(255,255,255,1)');
                grad.addColorStop(tInner, 'rgba(255,255,255,1)');
                const tMid1 = tInner + (1 - tInner) * 0.33;
                const tMid2 = tInner + (1 - tInner) * 0.66;
                grad.addColorStop(tMid1, 'rgba(255,255,255,0.22)');
                grad.addColorStop(tMid2, 'rgba(255,255,255,0.12)');
                grad.addColorStop(1, 'rgba(255,255,255,0)');

                    ctx.fillStyle = grad;
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, outerR, 0, Math.PI * 2);
                    ctx.fill();
                }
            } catch (error) {
                console.error('BrushSystem: Error during erase operation:', error);
                this.webglSafeMode = true;
            } finally {
                ctx.globalCompositeOperation = originalComposite;
            }
        } else {
            const hexColor = this.brushColor;
            const r = parseInt(hexColor.slice(1, 3), 16);
            const g = parseInt(hexColor.slice(3, 5), 16);
            const b = parseInt(hexColor.slice(5, 7), 16);

            // If effectively hard, paint as a solid circle (no falloff)
            if (outerR - innerR <= 2 * eps) {
                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 1)`;
                ctx.beginPath();
                ctx.arc(centerX, centerY, outerR, 0, Math.PI * 2);
                ctx.fill();
            } else {
                const grad = ctx.createRadialGradient(centerX, centerY, innerR, centerX, centerY, outerR);
                // Solid color up to innerR, then fade to 0 at edge
                // Always use full alpha in the gradient - opacity is handled at the canvas level
                const tInner = Math.min(0.999, Math.max(0, innerR / outerR));
                grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
                grad.addColorStop(tInner, `rgba(${r}, ${g}, ${b}, 1)`);
                // Two mid stops to elongate the soft transition
                const tMid1 = tInner + (1 - tInner) * 0.33;
                const tMid2 = tInner + (1 - tInner) * 0.66;
                grad.addColorStop(tMid1, `rgba(${r}, ${g}, ${b}, 0.22)`);
                grad.addColorStop(tMid2, `rgba(${r}, ${g}, ${b}, 0.12)`);
                grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(centerX, centerY, outerR, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    }
    
    drawTexturedBrush(ctx, centerX, centerY, radius) {
        const size = radius * 2;
        const x = centerX - radius;
        const y = centerY - radius;
        
        ctx.drawImage(this.brushTexture.image, x, y, size, size);
    }
    
    setBrushSize(size) {
        this.brushSize = Math.max(1, Math.min(500, size));
    }
    
    setBrushOpacity(opacity) {
        this.brushOpacity = Math.max(0, Math.min(1, opacity));
    }
    
    setBrushHardness(hardness) {
        this.brushHardness = Math.max(0, Math.min(1, hardness));
    }
    
    setBrushColor(color) {
        this.brushColor = color;
    }
    
    setBrushMode(mode) {
        if (mode === 'paint' || mode === 'erase') {
            this.brushMode = mode;
        }
    }
    
    setBrushTexture(texture) {
        this.brushTexture = texture;
    }
    
    isEnabled() {
        const enabled = this.texturePainter.mesh && 
               this.texturePainter.uvChannels.length > 0 &&
               this.texturePainter.projectionMode === 'brush';
        
        return enabled;
    }
    
    enableCursor(enabled = true) {
        this.cursorEnabled = enabled;
        if (!enabled) {
            this.hideBrushCursor();
        }
    }
    
    clearStrokes() {
        this.paintStrokes = [];
    }
    
    // Legacy no-op. Undo is centralized in texturePainter.undoManager
    storeCanvasState() {}
    
    // Legacy no-op. Use texturePainter.undo() instead.
    undoLastStroke() {
        this.texturePainter?.undo?.();
    }
    
    dispose() {
        if (this.brushCursor) {
            this.texturePainter.scene.remove(this.brushCursor);
            this.brushCursor.geometry.dispose();
            this.brushCursor.material.dispose();
        }
        
        if (this.brushTexture) {
            this.brushTexture.dispose();
        }
        
        this.clearStrokes();
    }
}
